/**
 * Created by jonas on 2021-02-09.
 *
 * Super basic Symbol implementation that extends PIXI.Sprite
 *
 */

import {DasUBoot} from "../DasUBoot";
import {DasUBootGameAssets} from "../DasUBootGameAssets";
import {gsap} from "gsap";
import {SpineGSAPWrapper} from "../../core/animation/SpineGSAPWrapper";
import {DasUBootReel} from "./DasUBootReel";
import {Positionable} from "../../core/reel/simplereel/SimpleReelModel";
import {DasUBootStatics} from "../utils/DasUBootStatics";
import {GraphicsPool} from "../utils/GraphicsPool";
import {GlowFrame} from "./symbols/symbolparts/GlowFrame";
import {MathUtils} from "../utils/MathUtils";
import {NineSlicePlane} from "pixi.js";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";

export enum DasUBootSymbolStates {
    NORMAL = "NORMAL",
    SPIN = "SPIN",
    START_SPIN = "START_SPIN",
    WIN = "WIN",
    NO_WIN = "NO_WIN",
    WIN_TOGGLE = "WIN_TOGGLE",
    EXPLODE = "EXPLODE",
    COLLAPSE = "COLLAPSE",
    DROP_IN = "DROP_IN",
    LANDING = "LANDING",
    REVEAL = "REVEAL",
    NUDGE = "NUDGE",
    STICKY = "STICKY",
    UN_STICK = "UN_STICK",
    INVISIBLE = "INVISIBLE",
    EXPAND = "EXPAND",
    FLASH = "FLASH",
}


export class DasUBootSymbol implements Positionable {

    public symbolName:string;
    public state:DasUBootSymbolStates;
    public reel:DasUBootReel;
    protected sprite:PIXI.Sprite;
    public view:PIXI.Container;
    protected maskedView:PIXI.Container;

    public position:PIXI.ObservablePoint;
    protected xWayOffset:PIXI.ObservablePoint;
    protected symbolFrame:GlowFrame | undefined;
    public isSubSymbol:boolean = false;
    protected _scaleMask:PIXI.Graphics;
    protected _whiteOverlay:PIXI.Sprite;
    protected frame:PIXI.NineSlicePlane;
    private periscopeHighlightLoop:gsap.core.Timeline | undefined;
    protected resizeScale:number = 1;
    protected _stateAnimation:gsap.core.Timeline;


    public getAvalancheScale() {
        return this.resizeScale;
    }

    public addAmtWaysCont(cont:PIXI.Container):void{
        cont.position.set(DasUBootStatics.symbolWidth/2, 30);
        this.view.addChild(cont);
    }
    public removeAmtWaysCont(cont:PIXI.Container):void{
        this.view.removeChild(cont);
    }


    public onPosition() {
        this.view.position.set(this.position.x, this.position.y + this.xWayOffset.y);
    }

    public onXWayOffset() {
        this.onPosition();
    }

    constructor(reel:DasUBootReel, symbolName:string) {
        this.position = new PIXI.ObservablePoint(this.onPosition, this, 0, 0);
        this.xWayOffset = new PIXI.ObservablePoint(this.onXWayOffset, this, 0, 0);
        this.sprite = new PIXI.Sprite();
        this.sprite.name = "mainSprite";
        this.sprite.position.set(72, 72);
        this.sprite.anchor.set(0.5, 0.5);
        this.reel = reel;
        if (symbolName == undefined){
            Logger.warn("This should not be undefined!");
            symbolName = "L1"
        }
        this.symbolName = symbolName;
        this.view = new PIXI.Container();
        this.view.name = `symbol.view(${this.symbolName})`;

        this._whiteOverlay = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._whiteOverlay.tint = DasUBootStatics.winHighlightTint;
        this._whiteOverlay.anchor.set(0.5, 0.5);
        this._whiteOverlay.alpha = 0;


        this.maskedView = new PIXI.Container();
        this.maskedView.name = `maskedView`;

        this._scaleMask = new PIXI.Graphics();
        this._scaleMask.name = "scaleMask";
        this._scaleMask.beginFill(0,1);
        this._scaleMask.drawRect(0,0,144,144);
        this._scaleMask.position.set(0, 0);
        this._scaleMask.visible = false;

        this.frame = new NineSlicePlane(DasUBoot.assetLoader.getTextures(DasUBootGameAssets.SHARP_SYMBOL_FRAME)[0], 4,4,4,4);
        this.frame.tint = DasUBootStatics.frameTintNormal;
        this.frame.pivot.set(72,72);
        this.frame.width = 144;
        this.frame.height = 144;

        this.sprite.addChild(this._whiteOverlay)
        this.sprite.addChild(this.frame);
        this.maskedView.addChild(this.sprite);


        this.view.addChild(this._scaleMask);
        this.view.addChild(this.maskedView);
    }

    protected getScaleYAnchor():number {
        return 0.5;
    }

    public resize(scale:number, offset:number, duration:number, customEase?:any):gsap.core.Timeline {

        this.resizeScale = scale;

        customEase = customEase != undefined ? customEase : DasUBootStatics.xWayScaleEase;

        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(() => {
            this.maskedView.mask = this._scaleMask;
            this._scaleMask.visible = true;
           // this.view.addChild(this._lines[0]);
           // this.view.addChild(this._lines[1]);
        });

        tl.add([
            gsap.to(this._scaleMask.scale, {duration:duration, ease:customEase, y:scale}),
            gsap.to(this.frame.scale, {duration:duration, ease:customEase, y:scale}),
            gsap.to(this.xWayOffset, {duration:duration, y:offset, ease:customEase}),
            gsap.to(this.maskedView.position, {
                duration:duration,
                y:((scale * 144) - 144) * this.getScaleYAnchor(),
                ease:customEase
            })
        ]);

        return tl;
    }


    public getXWayYOffset():number {
        return this.xWayOffset.y;
    }
    public resetSymbolPosition(reelPos:number, offset:number){
        this.position.y = DasUBootStatics.symbolHeight * reelPos;
        this.xWayOffset.y = offset;
    }

    public unMask(){
        this.maskedView.mask = null;
        this._scaleMask.visible = false;
    }

    public destroy(options?:{children?:boolean; texture?:boolean; baseTexture?:boolean}) {
        if (this._stateAnimation) {
            this._stateAnimation.kill();
        }
        this.deleteWinFrame();
        this.view.destroy(options);
    }

    public init(initState:DasUBootSymbolStates):DasUBootSymbol {
        this.getStateAnimation(initState);
        return this;
    }

    public moveToWinLayer() {
        if (this.isSubSymbol) {
            return;
        }
        this.reel.winLayer.addChild(this.view);
    }

    public moveToNormalLayer(topNormal:boolean = false) {
        if (this.isSubSymbol) {
            return;
        }
        if(topNormal){
            this.reel.normalOverLayer.addChild(this.view);
        } else {
            this.reel.normalLayer.addChild(this.view);
        }
    }

    public isVisibleInReel():boolean {
        for (let symbolData of this.reel.model.getAllVisibleSymbols()) {
            if (symbolData.positionable === this) {
                return true;
            }
        }
        return false;
    }


    protected setSpriteTexture(textureName?:string){
        if (textureName == "none") {
            this.sprite.texture = PIXI.Texture.EMPTY;
        } else {
            this.sprite.texture = DasUBoot.assetLoader.getTextures(DasUBootGameAssets.getSymbolStateTexturePath(this.symbolName, textureName))[0];
        }
    }

    protected setState(state:DasUBootSymbolStates, textureName?:string) {
        this.state = state;

        this.setSpriteTexture(textureName);

        this.sprite.scale.set(1, 1);

        this._whiteOverlay.width = this.sprite.width;
        this._whiteOverlay.height = this.sprite.height;

        this.updateFrameProperties(state)

        gsap.set(this.view, {alpha:1});
        this.hideOverlay(0.001);
    }


    private updateFrameProperties(state:DasUBootSymbolStates){
        if (state == DasUBootSymbolStates.WIN || state == DasUBootSymbolStates.WIN_TOGGLE){
            this.frame.tint = DasUBootStatics.frameTintWin;
        }else {
            this.frame.tint = DasUBootStatics.frameTintNormal;
        }

        this.frame.visible = (state != DasUBootSymbolStates.SPIN && state != DasUBootSymbolStates.EXPLODE );
    }

    public getStateAnimation(state:DasUBootSymbolStates):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        this._stateAnimation = tl;

        switch (state) {
            case DasUBootSymbolStates.SPIN:
                tl.add(() => this.setState(state, "_spin"));
                break;
            case DasUBootSymbolStates.START_SPIN:
                tl.add(() => this.setState(state));
                tl.add(() => this.moveToNormalLayer());
                tl.add(() => this.removeFrame());
                break;
            case DasUBootSymbolStates.LANDING:
                tl.add(() => this.setState(state));
                break;
            case DasUBootSymbolStates.WIN:
                tl.add(() => this.setState(state, "_win"));
                tl.add(() => this.moveToWinLayer());
                tl.add(this.getWin());
                break;
            case DasUBootSymbolStates.WIN_TOGGLE:
                tl.add(() => this.setState(state, "_win"));
                tl.add(() => this.moveToWinLayer());
                tl.add(this.getWinToggle());
                break;
            case DasUBootSymbolStates.NO_WIN:
                tl.add(() => this.setState(state));
                tl.add(this.showOverlay(DasUBootStatics.noWinTint, 0.3, 0.7, PIXI.BLEND_MODES.MULTIPLY));
                break;
            case DasUBootSymbolStates.EXPLODE:
                const explosion = this.getExplodeAnimation();
                tl.add(() => this.setState(state));
                tl.add(() => this.moveToWinLayer());
                tl.add(explosion, ">-0.0");
                tl.add(() => this.setState(state, "none"), "<");
                break;
            case DasUBootSymbolStates.COLLAPSE:
                tl.add(() => this.setState(state));
                break;
            case DasUBootSymbolStates.DROP_IN:
                tl.add(() => this.setState(state));
                break;
            case DasUBootSymbolStates.STICKY:
                tl.add(() => this.setState(state));
                tl.add(() => this.moveToWinLayer());
                tl.add(this.showOverlay(DasUBootStatics.noWinTint , 0.3, 0.7, PIXI.BLEND_MODES.NORMAL));
                break;
            case DasUBootSymbolStates.UN_STICK:
                tl.add(this.hideOverlay(0.2));
                tl.add(() => this.moveToWinLayer());
                tl.add(() => this.setState(state));
                break;
            case DasUBootSymbolStates.INVISIBLE:
                tl.add(() => this.setState(state));
                tl.add(gsap.to(this.view, {duration:0.2, alpha:0}));
                tl.add(() => this.moveToNormalLayer());
                break;
            default:
                tl.add(()=>this.deleteWinFrame());
                tl.add(() => this.setState(state));
        }

        if (tl.duration() == 0) {
            tl.add(gsap.to(this, {duration:0.0001}));
        }

        return this._stateAnimation;
    }


    private showOverlay(tint:number = DasUBootStatics.winHighlightTint,  duration:number = 0.05, alpha:number = 0.7, blendMode:PIXI.BLEND_MODES = PIXI.BLEND_MODES.ADD):gsap.core.Timeline{
        const tl = gsap.timeline();
        tl.add(()=>{
            this._whiteOverlay.tint = tint;
            this._whiteOverlay.blendMode = blendMode;
        });
        tl.add(gsap.to(this._whiteOverlay, {duration:duration, alpha:alpha, ease:"none"}));
        return tl;
    }

    private hideOverlay(duration:number = 0.6):gsap.core.Timeline{
        const tl = gsap.timeline();
        tl.add(gsap.to(this._whiteOverlay, {duration:duration, alpha:0, ease:"none"}));
        return tl;
    }


    public playPotentialWinAttention():gsap.core.Timeline{
        const tl = gsap.timeline();
        tl.add(this.showOverlay( DasUBootStatics.winHighlightTint, 0.05, 0.7, PIXI.BLEND_MODES.ADD));
        tl.add(this.hideOverlay(0.6));
        return tl;
    }

    public playPeriscopePotentialWin():void {
        if (this.periscopeHighlightLoop != undefined){
            this.periscopeHighlightLoop.play(0);
            return;
        }

        const winFrame = this.getWinFrame(DasUBootStatics.periscopeNearWinFrameTint);
        const tl:gsap.core.Timeline = gsap.timeline({repeat:-1});
        tl.add(()=>this.moveToWinLayer());
        tl.add(() => {this.view.addChild(winFrame)});
        tl.add(winFrame.show(0.1));
        tl.add(winFrame.getAttention(false), "<");
        this.periscopeHighlightLoop = tl;
    }

    public stopPeriscopePotentialWin(){
        if (this.periscopeHighlightLoop){
            this.periscopeHighlightLoop.kill();
            delete this.periscopeHighlightLoop;
            const winFrame = this.getWinFrame(DasUBootStatics.periscopeNearWinFrameTint);
            winFrame.hide(0.1);
        }
    }


    private getExplodeAnimation():gsap.core.Timeline {
        const explosionPool = DasUBoot.graphicsPool.getSpinePool(DasUBootGameAssets.SYMBOL_EXPLOSION_SPINE);
        const spine = explosionPool.withdraw();
        spine.position.set(DasUBootStatics.symbolWidth * 0.5, DasUBootStatics.symbolHeight * 0.5);
        const tl = gsap.timeline();
        tl.add(() => {
            this.view.addChild(spine);
            spine.scale.set(1, this.getAvalancheScale());
            spine.position.set(DasUBootStatics.symbolWidth * 0.5, DasUBootStatics.symbolHeight * this.getAvalancheScale() * 0.5);
            spine.updateTransform();
        });
        tl.add(SpineGSAPWrapper.createTimeline(spine, 0, "animation"));
        tl.add(() => {
            SpineGSAPWrapper.createTimeline(spine, 0, null);
            this.view.removeChild(spine);
            explosionPool.deposit(spine);
        });

        return tl;
    }

    public getShakeAnimation(duration:number = 1.715):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(gsap.to(this.position, {
            duration:duration,
            x:"-=3",
            ease:DasUBootStatics.bigWinAnticipationWiggle
        }));
        return tl.reverse();
    }


    private getWinToggle():gsap.core.Timeline {
        let tl:gsap.core.Timeline = gsap.timeline();
        const winFrame = this.getWinFrame();
        tl.add(winFrame.hide());
        tl.add(() => {this.view.addChild(winFrame)});
        tl.add(winFrame.show(0.4));
        tl.add(winFrame.getLoopAnimation(), "<");
        tl.add(winFrame.hide(0.4), ">-0.4");

        if(DasUBoot.apiPlugin.gameClientConfiguration.belowStakeWinRestriction && DasUBoot.model.winRatio <= 1){
            const dur = tl.duration();
            tl.kill();
            tl = gsap.timeline();
            tl.add(gsap.to(this, {duration:dur}));
        }

        return tl;
    }

    protected getWin():gsap.core.Timeline {
        let tl:gsap.core.Timeline = gsap.timeline();
        const winFrame = this.getWinFrame();
        tl.add(() => {this.view.addChild(winFrame)});
        tl.add(winFrame.show(0.1));
        tl.add(winFrame.getWinAnimation(), "<");

        if(DasUBoot.apiPlugin.gameClientConfiguration.belowStakeWinRestriction && DasUBoot.model.winRatio <= 1){
            const dur = tl.duration() * 0.5;
            tl.kill();
            tl = gsap.timeline();
            tl.add(gsap.to(this, {duration:dur}));
        }


        return tl;
    }



    private deleteWinFrame() {
        if (this.symbolFrame) {
            this.view.removeChild(this.symbolFrame);
            DasUBoot.graphicsPool.getGenericPool(GraphicsPool.SYMBOL_FRAME).deposit(this.symbolFrame);
            delete this.symbolFrame;
        }
    }

    private removeFrame() {
        if (this.symbolFrame != undefined) {
            this.view.removeChild(this.symbolFrame);
        }
    }

    protected getWinFrame(tint:number = DasUBootStatics.glowFrameWinTint):GlowFrame {
        if (this.symbolFrame == undefined) {
            this.symbolFrame = <GlowFrame>DasUBoot.graphicsPool.getGenericPool(GraphicsPool.SYMBOL_FRAME).withdraw();
        }
        this.symbolFrame.frameSize.set(144, this.resizeScale * 144);
        this.symbolFrame.pivot.set(72, 72);
        this.symbolFrame.position.set(72, 72);
        this.symbolFrame.tint = tint;
        return this.symbolFrame;
    }


    public resetFrame(){
        if (this.symbolFrame != undefined) {
            this.symbolFrame.hide(0.2);
        }
    }

    getGlobalPosition(center:boolean = false) {
        if (center){
            return MathUtils.addPoints(MathUtils.addPoints(this.reel.position, this.position), new PIXI.Point(DasUBootStatics.symbolWidth*0.5, DasUBootStatics.symbolHeight*0.5));
        }
        return MathUtils.addPoints(this.reel.position, this.position);
    }

}